import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '@packages/utils';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import { workshopsApi } from 'redux/apiSlice/workshops.slice';
import { Table } from 'components/Base/Table';
import {
  ERegistrantsColumnKey,
  TRegistrantUI,
} from 'data/types/registrants.types';
import {
  useGetRegistrantsQuery,
  useGetValidateExportQuery,
  useExportRegistrantsMutation,
} from 'redux/apiSlice/registrants.slice';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { TabBodyWrapper } from 'components/Base/Tabs';
import { EWorkshopStatus } from 'data/types/workshops.types';
import { EWorkshopLanguage } from 'data/types/generic.types';
import DeleteRegistrantModal from 'components/Shared/DeleteRegistrantModal/DeleteRegistrantModal';
import { useDeleteRegistrant } from 'hooks/useDeleteRegistrant';
import { EUserType } from 'data/types/user.types';
import { useAppSelector } from 'redux/store';
import { Flex } from 'antd';
import Button from 'components/Base/Button/Button';
import { EditPencilOutline, PersonRemoveOutline } from 'assets/icon/icon';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import useGetRegistrantsColumnsData from './RegistrantsList.data';
import { TRegistrantsListProps } from './RegistrantsList.types';
import useAgeGroupLanguageFilter from './useAgeGroupLanguageFilter';
import Attendances from './Attendances';
import EditRegistrantModal from './EditRegistrantModal/EditRegistrantModal';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  creationDate: 'registrationDate',
  registrantFullName: 'registrantName',
  phone: 'registrantMobile',
  email: 'registrantEmail',
  additionalAdultFullName: 'additionalAdult',
  additionalAdultMobile: 'adultMobile',
  additionalAdultEmail: 'adultEmail',
  childCount: 'countOfChildren',
  childrenNames: 'childrenNames',
  [ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP]: 'ageGroup',
  customQuestionAnswer: 'questionAnswer',
  orientationKit: 'kits',
  binderReceived: 'binder',
};

const SORT_FIELD_MAP: Record<string, string> = {
  creationDate: 'CREATION_DATE',
  registrantFullName: 'FIRST_NAME',
};

const ADMIN_TYPES = [
  EUserType.READY_ADMIN,
  EUserType.SUPER_ADMIN,
  EUserType.ELC_LEAD,
  EUserType.ELC_PM,
];

// Per GROW-3866, only RFK Admins can remove registrants on workshops.
const RFK_ADMIN_TYPES = [EUserType.READY_ADMIN, EUserType.SUPER_ADMIN];

const RegistrantsList: FC<TRegistrantsListProps> = ({
  workshopId = '',
  workshop,
  customQuestion,
  hideChildInfo,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRegistrant, setSelectedRegistrant] =
    useState<TRegistrantUI | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { deleteRegistrant, isLoading: isDeleteLoading } =
    useDeleteRegistrant();

  const usersData = useAppSelector(state => state.userSlice);

  const isAdmin = useMemo(() => {
    const user = usersData?.user;
    return user?.type?.some((type: EUserType) => ADMIN_TYPES.includes(type));
  }, [usersData?.user]);

  const isRfkAdmin = useMemo(() => {
    const user = usersData?.user;
    return user?.type?.some((type: EUserType) =>
      RFK_ADMIN_TYPES.includes(type),
    );
  }, [usersData?.user]);

  const handleEditRegistrant = useCallback((registrant: TRegistrantUI) => {
    if (registrant.isWorkshopClosed || registrant.minimalRegistration) return;
    setSelectedRegistrant(registrant);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((record: TRegistrantUI) => {
    if (record.isWorkshopClosed) return;
    setSelectedRegistrant(record);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (selectedRegistrant && workshopId) {
      const success = await deleteRegistrant({
        workshopId,
        registrantId: selectedRegistrant.registrantId,
      });

      if (success) {
        setIsDeleteModalOpen(false);
        setSelectedRegistrant(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedRegistrant(null);
  };
  const { workshopRegistrantsColumns } = useGetRegistrantsColumnsData(
    handleEditRegistrant,
    handleDeleteClick,
  );
  const { data: isExportValid } = useGetValidateExportQuery(
    {
      workshopId,
    },
    {
      skip: !workshopId,
    },
  );

  const params = useParams();
  const { id } = params as { id: string };

  const { data: workshopDetailsCachedData } =
    workshopsApi.endpoints.getWorkshopById.useQueryState(
      {
        id,
      },
      { skip: !id },
    );

  const workshopClasses = workshopDetailsCachedData?.classes.value;
  const workshopStatus = workshopDetailsCachedData?.status;

  const storageKey = `${ETableStoragePaths.WORKSHOP_REGISTRANTS}-${workshopId}`;

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TRegistrantUI>({
    storagePath: storageKey,
    defaultColumns: workshopRegistrantsColumns,
  });

  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.workshop-registrants',
  });

  const { data, isFetching, isLoading, isSuccess } = useGetRegistrantsQuery(
    {
      page: 0,
      pageSize: 2000,
      sortColumn,
      filters: selectedFilters,
      workshopId,
    },
    {
      skip: !workshopId,
    },
  );

  const ageGroupLanguageFilters = useAgeGroupLanguageFilter(workshopClasses);

  const isReadonly = useMemo(
    () =>
      workshopStatus !== EWorkshopStatus.PUBLISHED ||
      !!data?.allowedOperations.readonly,
    [workshopStatus, data?.allowedOperations.readonly],
  );

  const computedColumns = useMemo(() => {
    if (!isSuccess) return null;

    let cols = workshopRegistrantsColumns.map(item => {
      if (item.key === 'attendances') {
        return {
          ...item,
          render: (_: unknown, record: TRegistrantUI) => {
            const isEditable = !record.isWorkshopClosed;
            const isDeletable =
              !record.hasAttendedWorkshop &&
              !record.isWorkshopClosed &&
              !record.nonDeleteReason;

            return (
              <Flex align="center" gap={24}>
                <Flex gap={4}>
                  {/* GROW-4363: minimal registrants have no contact info to
                      edit and no upgrade path, so the Edit action is hidden. */}
                  {!record.minimalRegistration && (
                    <Button
                      variant="ghost"
                      aria-label="Edit Registrant"
                      tooltip={isEditable ? 'Edit Registrant' : undefined}
                      icon={
                        <EditPencilOutline
                          size={20}
                          fill={isEditable ? '#656565' : '#808080'}
                        />
                      }
                      onClick={() => handleEditRegistrant(record)}
                      disabled={!isEditable}
                      className={!isEditable ? 'cursor-not-allowed' : ''}
                    />
                  )}

                  {isRfkAdmin &&
                    (isDeletable ? (
                      <Button
                        variant="ghost"
                        tooltip="Delete Registrant"
                        icon={<PersonRemoveOutline size={24} fill="#656565" />}
                        onClick={() => handleDeleteClick(record)}
                        disabled={
                          record.isWorkshopClosed === false &&
                          record.hasAttendedWorkshop
                        }
                        className={
                          record.isWorkshopClosed === false &&
                          record.hasAttendedWorkshop
                            ? 'cursor-not-allowed'
                            : ''
                        }
                      />
                    ) : (
                      <Button
                        variant="ghost"
                        tooltip={
                          record.nonDeleteReason || "Can't delete registrant"
                        }
                        icon={<PersonRemoveOutline size={24} fill="#808080" />}
                        disabled
                        className="cursor-not-allowed"
                      />
                    ))}
                </Flex>

                <div className="pe-auto">
                  <Attendances
                    isReadonly={isReadonly}
                    key={record.registrantId}
                    attendances={record.attendances}
                    fullName={record.fullName}
                    hasUpdatableAttendance={record.hasUpdatableAttendance}
                    registrantId={record.registrantId}
                    workshopClassId={record.workshopClassId}
                    orientationKit={record.orientationKit}
                    hasStartedPart={!!workshopDetailsCachedData?.hasStartedPart}
                    materialsLanguage={record.materialsLanguage}
                    workshopId={workshopId}
                    registrantStatus={record.registrantStatus}
                  />
                </div>
              </Flex>
            );
          },
        };
      }
      if (
        item.key === ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP &&
        ageGroupLanguageFilters
      ) {
        return {
          ...item,
          filters: ageGroupLanguageFilters,
        };
      }
      return item;
    });

    const hasQuestionAnswers = data?.content?.some(r => r.customQuestionAnswer);
    const showQuestionColumn = !!customQuestion || !!hasQuestionAnswers;

    if (!showQuestionColumn || hideChildInfo) {
      cols = cols.filter(col => {
        if (!showQuestionColumn && col.dataIndex === 'customQuestionAnswer') {
          return false;
        }
        if (
          hideChildInfo &&
          (col.dataIndex === 'countOfChildren' ||
            col.dataIndex === 'childrenNames')
        ) {
          return false;
        }
        return true;
      });
    }

    return cols;
  }, [
    isSuccess,
    isReadonly,
    workshopStatus,
    workshopDetailsCachedData?.hasStartedPart,
    hideChildInfo,
    customQuestion,
    ageGroupLanguageFilters,
    workshopRegistrantsColumns,
    workshopId,
    data?.content,
  ]);

  useEffect(() => {
    if (computedColumns) {
      setColumns(computedColumns);
    }
  }, [computedColumns]);

  const handleEditSuccess = () => {
    // Refresh your data or perform any necessary actions after successful update
    // For example:
    // refetchRegistrants();
  };

  const [exportRegistrants, { isLoading: isExporting }] =
    useExportRegistrantsMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[storageKey] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, [storageKey]);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {
        workshopId,
      };
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.registrantFullName?.[0]) {
        filters.registrantFullName = (
          selectedFilters.registrantFullName[0] as string
        )
          .toString()
          .trim();
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = (selectedFilters.email[0] as string).toString().trim();
      }
      if (selectedFilters?.additionalAdultFullName?.[0]) {
        filters.additionalAdultFullName = (
          selectedFilters.additionalAdultFullName[0] as string
        )
          .toString()
          .trim();
      }
      if (selectedFilters?.additionalAdultEmail?.[0]) {
        filters.additionalAdultEmail = (
          selectedFilters.additionalAdultEmail[0] as string
        )
          .toString()
          .trim();
      }
      const groupedFilter =
        selectedFilters?.[
          ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP
        ];
      if (groupedFilter?.length) {
        const allLanguages = Object.values(EWorkshopLanguage) as string[];
        const parentGroupValues = [
          ERegistrantsColumnKey.AGE_GROUP as string,
          ERegistrantsColumnKey.LANGUAGE as string,
        ];
        const ageGroups = groupedFilter.filter(
          v =>
            !allLanguages.includes(v as string) &&
            !parentGroupValues.includes(v as string),
        );
        const languages = groupedFilter.filter(v =>
          allLanguages.includes(v as string),
        );
        if (ageGroups.length) {
          filters.ageGroups = ageGroups;
        }
        if (languages.length) {
          filters.languages = languages;
        }
      }
      return filters;
    },
    [workshopId, selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const sort = buildExportSort();
      await exportRegistrants({
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportRegistrants,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;
    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const sort = buildExportSort();
      await exportRegistrants({
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(ids),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    selectedRows,
    exportRegistrants,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <>
      <TabBodyWrapper
        isEmpty={!data?.content.length && !hasSelectedFilter && !isFetching}
        isLoading={isLoading}
        emptyText={t('empty-view.TITLE')}
      >
        <Table<TRegistrantUI>
          {...(isExportValid && { headerActions: [exportAction] })}
          tabHeight={44}
          tableTitle={t('TITLE')}
          onFiltersReset={onFiltersReset}
          hasFilters={hasFilters}
          currentPage={page}
          columns={columns}
          dataSource={data?.content}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements}
          onChange={handleTableChange}
          rowKey={record => record.registrantId}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          rowClassName={record => {
            let className = '';

            if (record.isWorkshopClosed) {
              className += 'disabled-registrant-row ';
            }

            if (!isAdmin) {
              className += 'pe-none ';
            }

            return className.trim();
          }}
          storagePath={ETableStoragePaths.WORKSHOP_REGISTRANTS}
        />
      </TabBodyWrapper>
      {selectedRegistrant && (
        <EditRegistrantModal
          open={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          registrant={selectedRegistrant}
          workshop={workshop}
          onSuccess={handleEditSuccess}
        />
      )}

      <DeleteRegistrantModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
      />
    </>
  );
};

export default RegistrantsList;
